/**
 * Stall watchdog rule for a live page whose updates are gated behind ref-counted pauses.
 *
 * The Live Maneuvers merge loop runs only while every pause reason in `liveManeuverPollCoordinator`
 * is clear, and each reason has a release path that MUST run for updates to continue: a chart/map
 * panel reporting that it finished loading, a refilter settling the interaction that triggered it, a
 * fetch's `finally`. Each of those is one missed edge away from being held forever.
 *
 * Live Maneuvers has NO push channel of its own — the only control frame this app sends is
 * `events_aggregate_ready`, which belongs to Live Performance — so that merge timer is the page's
 * one and only way of learning anything. A held pause stops it dead, and the page then sits on stale
 * rows with Follow still reading LIVE, recoverable only by a reload.
 *
 * Individual leaks get fixed as they are found. This bounds the cost of the NEXT one to a single
 * check interval instead of the rest of the session.
 *
 * NOT `liveStallBackoff`, despite the name: that one SLOWS polls down when the feed has gone quiet.
 * This one restarts a loop that has stopped for a reason internal to the page.
 *
 * Pure module — no Solid, no DOM, no network — the same shape as `liveManeuverPollCoordinator`.
 */

/**
 * Cadence of the stall check.
 *
 * It has to be its own timer: the merge timer is the thing that stops when a pause leaks, so it can
 * never be what notices. Cheap enough to run unconditionally — the check reads a few flags and
 * returns immediately on a healthy page.
 */
export const LIVE_STALL_CHECK_MS = 10_000;

/** Floor for the stall threshold, so a fast configured cadence cannot make the watchdog twitchy. */
export const LIVE_STALL_MIN_WINDOW_MS = 30_000;

/** Margin added on top, covering the round trip of the poll that is allowed to be in flight. */
export const LIVE_STALL_MARGIN_MS = 10_000;

/**
 * How long the loop may make no progress before it counts as stalled.
 *
 * Derived from the configured cadence, never hard-coded: RaceSight polls Influx roughly every 5 s
 * (`liveConfigStore.pollIntervalMs()`), so the floor is what governs here and the threshold lands at
 * 40 s. It must clear a HEALTHY gap comfortably or the watchdog would fight normal operation — the
 * worst healthy gap is one whole cadence plus a round trip. Two cadences plus a margin leaves room
 * for a slow response without ever reaching a real interaction: menus settle in well under a second,
 * so a pause outstanding this long is not someone mid-gesture.
 */
export function resolveLiveStallThresholdMs(pollIntervalMs: number): number {
  const poll = Number.isFinite(pollIntervalMs) && pollIntervalMs > 0 ? pollIntervalMs : LIVE_STALL_MIN_WINDOW_MS;
  return Math.max(poll * 2, LIVE_STALL_MIN_WINDOW_MS) + LIVE_STALL_MARGIN_MS;
}

export type LiveLoopProgressState = {
  /** The page is in live mode. */
  live: boolean;
  /** The user is following the live edge (the report Follow toggle is on). */
  following: boolean;
  /** A full fetch has landed, so merge polls are allowed at all. */
  initialLoadDone: boolean;
  /** A fetch is in flight right now. */
  fetchInFlight: boolean;
  /**
   * How long the OLDEST in-flight fetch has been running. Omitted (or 0) means "not tracked", which
   * keeps the lenient behaviour: any fetch in flight counts as progress.
   */
  fetchInFlightForMs?: number;
  /** Since the loop last PROVED it was alive: a poll started, or a fetch finished. */
  stalledForMs: number;
  /** From `resolveLiveStallThresholdMs`. */
  thresholdMs: number;
};

/**
 * Whether the live loop should be force-released.
 *
 * `following` is load-bearing: with follow off the user has ASKED for no updates, so a loop making no
 * progress is the correct state, and forcing it would resurrect a pause they chose.
 *
 * A fetch in flight is itself progress — its `finally` re-stamps the clock — but only while that
 * fetch is still young enough to be believable. `fetch()` has no timeout of its own, so a request
 * whose connection dies without an RST (a boat link dropping, a VPN re-keying, a lid closed on a
 * half-open socket) leaves a promise that never settles. Such a request both stops the merge loop AND
 * disarms the watchdog meant to restart it, so past the threshold it is not progress — it IS the
 * stall.
 */
export function shouldForceReleaseLiveLoop(state: LiveLoopProgressState): boolean {
  if (!state.live || !state.following || !state.initialLoadDone) { return false; }
  if (state.fetchInFlight && (state.fetchInFlightForMs ?? 0) < state.thresholdMs) { return false; }
  return state.stalledForMs >= state.thresholdMs;
}
